import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Paperclip, X } from "lucide-react";

interface CommissionInquiryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: number | null;
}

const MAX_NOTES_LENGTH = 250;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILES = 5;

export function CommissionInquiryDialog({
  open,
  onOpenChange,
  invoiceId,
}: CommissionInquiryDialogProps) {
  const { toast } = useToast();
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [checkNumber, setCheckNumber] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const resetForm = useCallback(() => {
    setPaymentMethod("");
    setPaymentDate("");
    setCheckNumber("");
    setPaymentAmount("");
    setNotes("");
    setFiles([]);
  }, []);

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) resetForm();
      onOpenChange(open);
    },
    [onOpenChange, resetForm],
  );

  const submitMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await fetch("/api/invoices/commission-inquiry", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          body.message ||
            `Failed to submit commission inquiry (HTTP ${res.status})`,
        );
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Inquiry Submitted",
        description:
          "Your commission inquiry has been sent to the support team.",
      });
      handleClose(false);
    },
    onError: (err: Error) => {
      toast({
        title: "Submission Failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleFileAdd = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const incoming = Array.from(e.target.files ?? []);
      const valid = incoming.filter(
        (f) => f.size <= MAX_FILE_SIZE_MB * 1024 * 1024,
      );
      if (valid.length < incoming.length) {
        toast({
          title: "File too large",
          description: `Max file size is ${MAX_FILE_SIZE_MB}MB. Some files were skipped.`,
          variant: "destructive",
        });
      }
      setFiles((prev) => {
        const combined = [...prev, ...valid];
        const trimmed = combined.slice(0, MAX_FILES);
        const dropped = combined.length - trimmed.length;
        if (dropped > 0) {
          toast({
            title: "Attachment limit reached",
            description: `Only ${MAX_FILES} attachments allowed. ${dropped} file(s) were not added.`,
            variant: "destructive",
          });
        }
        return trimmed;
      });
      e.target.value = "";
    },
    [toast],
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const isValid =
    paymentMethod.trim() !== "" &&
    paymentDate.trim() !== "" &&
    checkNumber.trim() !== "" &&
    paymentAmount.trim() !== "";

  const handleSubmit = () => {
    if (!invoiceId || !isValid) return;
    const formData = new FormData();
    formData.append("invoiceId", String(invoiceId));
    formData.append("paymentMethod", paymentMethod);
    formData.append("paymentDate", paymentDate);
    formData.append("checkNumber", checkNumber);
    formData.append("paymentAmount", paymentAmount);
    if (notes.trim()) formData.append("notes", notes.trim());
    for (const file of files) {
      formData.append("attachments", file);
    }
    submitMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Commission Inquiry Form</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground pt-1">
            Complete this form if you believe a supplier has paid commission on
            an invoice you created, but the commission was not properly credited
            to you. Before submitting, please contact the supplier and gather the
            necessary payment details to help us investigate your inquiry.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Payment Method */}
          <div className="space-y-1.5">
            <Label htmlFor="ci-payment-method">
              Method commission was paid <span className="text-destructive">*</span>
            </Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger id="ci-payment-method">
                <SelectValue placeholder="Select method..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Direct Deposit">Direct Deposit</SelectItem>
                <SelectItem value="Check">Check</SelectItem>
                <SelectItem value="Paid by Third Party">
                  Paid by Third Party
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Payment Date */}
          <div className="space-y-1.5">
            <Label htmlFor="ci-payment-date">
              Date commission was paid <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ci-payment-date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>

          {/* Check Number */}
          <div className="space-y-1.5">
            <Label htmlFor="ci-check-number">
              Supplier check number <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ci-check-number"
              placeholder='Enter check number or "None"'
              value={checkNumber}
              onChange={(e) => setCheckNumber(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              If you do not know the check number, write None.
            </p>
          </div>

          {/* Payment Amount */}
          <div className="space-y-1.5">
            <Label htmlFor="ci-payment-amount">
              Total dollar amount of the payment sent{" "}
              <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                $
              </span>
              <Input
                id="ci-payment-amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                className="pl-7"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="ci-notes">
              Additional notes{" "}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              id="ci-notes"
              placeholder="Any additional context..."
              value={notes}
              onChange={(e) =>
                setNotes(e.target.value.slice(0, MAX_NOTES_LENGTH))
              }
              rows={3}
              className="text-sm"
            />
            <p className="text-xs text-muted-foreground text-right">
              {notes.length}/{MAX_NOTES_LENGTH}
            </p>
          </div>

          {/* Attachments */}
          <div className="space-y-1.5">
            <Label>
              Attachments{" "}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={files.length >= MAX_FILES}
                onClick={() =>
                  document.getElementById("ci-file-input")?.click()
                }
              >
                <Paperclip className="h-3.5 w-3.5" />
                Attach file
              </Button>
              <span className="text-xs text-muted-foreground">
                Max {MAX_FILES} files, {MAX_FILE_SIZE_MB}MB each
              </span>
            </div>
            <input
              id="ci-file-input"
              type="file"
              multiple
              className="hidden"
              onChange={handleFileAdd}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            />
            {files.length > 0 && (
              <div className="space-y-1 mt-1">
                {files.map((file, i) => (
                  <div
                    key={`${file.name}-${i}`}
                    className="flex items-center gap-2 rounded-md border px-2 py-1 text-xs"
                  >
                    <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="truncate flex-1">{file.name}</span>
                    <span className="text-muted-foreground shrink-0">
                      {(file.size / 1024).toFixed(0)}KB
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Remove attachment ${file.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || submitMutation.isPending}
          >
            {submitMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Submit Inquiry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
